/**
 * Sentinel stored in `workspace_filter` when the Unmapped checkbox is checked.
 * Empty filter = show all (mapped + unmapped). Non-empty filter shows only
 * selected division ids and Unmapped when this sentinel is present.
 */
export const WORKSPACE_FILTER_UNMAPPED_ID = 0;

/**
 * @param {unknown[]} ids
 * @returns {number[]}
 */
export function normalizeWorkspaceFilterIds(ids) {
    const seen = new Set();
    const normalized = [];

    for (const rawId of ids ?? []) {
        const id = Number(rawId);
        if (!Number.isFinite(id) || seen.has(id)) {
            continue;
        }
        // Allow positive division ids and the Unmapped sentinel (0).
        if (id !== WORKSPACE_FILTER_UNMAPPED_ID && id <= 0) {
            continue;
        }
        seen.add(id);
        normalized.push(id);
    }

    return normalized;
}

/**
 * Whether the Unmapped checkbox is selected in the filter.
 * Empty filter means show-all (checkbox stays unchecked).
 *
 * @param {unknown[]} filterIds
 * @returns {boolean}
 */
export function workspaceFilterIncludesUnmapped(filterIds) {
    return normalizeWorkspaceFilterIds(filterIds).includes(
        WORKSPACE_FILTER_UNMAPPED_ID,
    );
}

/**
 * @param {unknown[]} filterIds
 * @returns {number[]}
 */
export function withUnmappedIncludedInWorkspaceFilter(filterIds) {
    const normalized = normalizeWorkspaceFilterIds(filterIds);
    if (normalized.includes(WORKSPACE_FILTER_UNMAPPED_ID)) {
        return normalized;
    }

    return [...normalized, WORKSPACE_FILTER_UNMAPPED_ID];
}

/**
 * @param {unknown[]} filterIds
 * @returns {number[]}
 */
export function withUnmappedExcludedFromWorkspaceFilter(filterIds) {
    return normalizeWorkspaceFilterIds(filterIds).filter(
        (id) => id !== WORKSPACE_FILTER_UNMAPPED_ID,
    );
}

/**
 * Space categories visible under the current division / Unmapped filter.
 * Empty selection shows all categories. Otherwise only checked divisions and
 * Unmapped (when selected) are shown.
 *
 * @param {Array<{ division_id?: unknown }|null|undefined>|null|undefined} spaceCategories
 * @param {unknown[]} filterIds
 * @returns {Array}
 */
export function filterSpaceCategoriesByWorkspaceFilter(
    spaceCategories,
    filterIds,
) {
    const tree = Array.isArray(spaceCategories) ? spaceCategories : [];
    const normalized = normalizeWorkspaceFilterIds(filterIds);

    if (!normalized.length) {
        return tree;
    }

    const includeUnmapped = normalized.includes(WORKSPACE_FILTER_UNMAPPED_ID);
    const idSet = new Set(normalized.filter((id) => id > 0));

    return tree.filter((cat) => {
        const divisionId = Number(cat?.division_id);
        if (!Number.isFinite(divisionId) || divisionId <= 0) {
            return includeUnmapped;
        }

        return idSet.has(divisionId);
    });
}

/**
 * Resolve the Space Group id for the current Work selection (category or space).
 *
 * @param {{
 *   activeWorkspaceCategoryId?: unknown,
 *   activeWorkspace?: { workspaceCategoryId?: unknown, workspace_category_id?: unknown }|null,
 * }} selection
 * @returns {number|null}
 */
export function resolveWorkspaceCategoryIdForSelection(selection = {}) {
    const fromCategory = Number(selection.activeWorkspaceCategoryId);
    if (Number.isFinite(fromCategory) && fromCategory > 0) {
        return fromCategory;
    }

    const fromSpace = Number(
        selection.activeWorkspace?.workspaceCategoryId ??
            selection.activeWorkspace?.workspace_category_id,
    );

    return Number.isFinite(fromSpace) && fromSpace > 0 ? fromSpace : null;
}

/**
 * Whether applying `filterIds` should clear the current Work selection.
 * Empty filter = show-all (keep selection). Non-empty filter clears when the
 * selected Space Group is no longer visible — including when the filter yields
 * no Space Groups at all.
 *
 * @param {{
 *   filterIds?: unknown[],
 *   spaceCategories?: Array<{ id?: unknown, division_id?: unknown }>|null,
 *   activeWorkspaceCategoryId?: unknown,
 *   activeWorkspace?: {
 *     id?: unknown,
 *     workspaceCategoryId?: unknown,
 *     workspace_category_id?: unknown,
 *   }|null,
 *   hasActiveBoardOrItem?: boolean,
 * }} options
 * @returns {boolean}
 */
export function shouldClearWorkSelectionForWorkspaceFilter({
    filterIds = [],
    spaceCategories = [],
    activeWorkspaceCategoryId = null,
    activeWorkspace = null,
    hasActiveBoardOrItem = false,
} = {}) {
    const categoryId = resolveWorkspaceCategoryIdForSelection({
        activeWorkspaceCategoryId,
        activeWorkspace,
    });

    const hasSelection =
        categoryId != null ||
        Boolean(activeWorkspace?.id) ||
        Boolean(hasActiveBoardOrItem);

    if (!hasSelection) {
        return false;
    }

    const normalized = normalizeWorkspaceFilterIds(filterIds);
    // Empty filter = show-all; keep the current selection.
    if (!normalized.length) {
        return false;
    }

    if (categoryId == null) {
        return true;
    }

    const visible = filterSpaceCategoriesByWorkspaceFilter(
        spaceCategories,
        normalized,
    );
    return !visible.some((cat) => Number(cat?.id) === categoryId);
}
